<script setup lang="ts">
import formatPrice from '@/helpers/formatPrice'
import type { Payment, StageCode, StageSetup, ThankYouPage } from '@/types'

interface Props {
  stageCode: StageCode
  stage: StageSetup
  orderNumber?: string
  date: Payment['status_change_at']
  providerTitle: Payment['provider']['title']
  providerType: Payment['provider']['type']
  totalPrice: Payment['sum']
  ofdReceiptUrl?: Payment['ofd_receipt_url']
  returnUrl?: Payment['return_url']
  thp?: ThankYouPage
}

const props = defineProps<Props>()
</script>

<template>
  <div class="ui-table">
    <span class="ui-table__title" v-html="thp?.subtitle ? thp.subtitle : stage.subtitle" />
    <span class="ui-table__description" v-html="thp?.description ? thp.description : stage.description" />

    <ul class="ui-table-info">
      <li v-if="orderNumber" class="ui-table-info-item">
        <span class="ui-table-info-item__key">Номер платежа</span>
        <span class="ui-table-info-item__value">{{ orderNumber }}</span>
      </li>

      <li v-if="date" class="ui-table-info-item">
        <span class="ui-table-info-item__key">Дата и время</span>
        <span class="ui-table-info-item__value">{{ date }}</span>
      </li>

      <li v-if="providerTitle" class="ui-table-info-item">
        <span class="ui-table-info-item__key">Способ оплаты</span>
        <span class="ui-table-info-item__value">{{ providerTitle }}</span>
      </li>

      <li v-if="providerType" class="ui-table-info-item">
        <span class="ui-table-info-item__key">Метод оплаты</span>
        <span class="ui-table-info-item__value">{{ providerType }}</span>
      </li>

      <li v-if="totalPrice" class="ui-table-info-item">
        <span class="ui-table-info-item__key">Сумма</span>
        <span class="ui-table-info-item__value">{{ formatPrice(totalPrice, false) }}</span>
      </li>
    </ul>

    <a v-if="ofdReceiptUrl" :href="ofdReceiptUrl" target="_blank" class="ui-table__receipt">
      Кассовый чек
    </a>

    <a v-if="thp?.url" :href="thp.url" target="_blank" class="ui-table__action">
      <template v-if="thp?.call_to_action">{{ thp.call_to_action }}</template>
      <template v-else>Вернуться</template>
    </a>

    <a
      v-if="!['Success', 'WaitSign'].includes(stageCode) && returnUrl"
      :href="returnUrl"
      target="_blank"
      class="ui-table__action"
    >
      Вернуться
    </a>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/variables';

.ui-table {
  display: flex;
  flex-direction: column;
  align-items: center;

  background: $c-white;
  border-radius: 20px;

  margin: ($font-size-base * 2) auto;
  padding: ($font-size-base * 2);
  max-width: 600px;
  width: 100%;

  &__title {
    display: block;
    margin-bottom: $font-size-base;
    text-align: center;

    @include font-size(1.4rem, 600, 1.4);
  }

  &__description {
    display: block;
    margin-bottom: $font-size-base * 3;
    text-align: center;

    @include font-size(1rem, 500, 1.5);
  }

  &-info {
    width: 100%;
    margin-bottom: $font-size-base * 3;

    &-item {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;

      border-bottom: 1px solid #97aab5;
      padding: $font-size-base 0;

      &__key,
      &__value {
        @include font-size(0.9rem, 400, 1.5);
      }

      &__key {
        font-weight: 500;
      }
    }
  }

  &__receipt {
    color: #009bff;
    text-decoration: none;
    transition-duration: 0.3s;
    margin-bottom: $font-size-base * 3;

    @include font-size(0.9rem, 600, 1.5);

    &:hover {
      opacity: 0.6;
    }
  }

  &__action {
    display: block;
    border-radius: 10px;
    background: #009bff;
    padding: $font-size-base * 2 $font-size-base * 2;
    width: 100%;
    text-align: center;
    color: $c-white;
    transition-duration: 0.3s;
    text-decoration: none;
    margin-bottom: $font-size-base * 3;
    box-sizing: border-box;

    @include font-size(1rem, 600, 1.4);

    &:hover {
      opacity: 0.8;
    }

    &:disabled {
      pointer-events: none;
      opacity: 0.6;
    }
  }

  @media screen and (max-width: $breakpoint-md) {
    & {
      margin: ($font-size-base * 2) auto;
      padding: ($font-size-base * 2) ($font-size-base);
    }

    &__title {
      @include font-size(1.2rem, 600, 1.4);
    }

    &__description {
      @include font-size(0.9rem, 500, 1.4);
    }

    &-info {
      &-item {
        &__key,
        &__value {
          @include font-size(0.85rem, 400, 1.4);
        }

        &__key {
          font-weight: 500;
        }
      }
    }

    &__receipt {
      margin-bottom: $font-size-base * 2;

      @include font-size(0.85rem, 600, 1.4);
    }

    &__action {
      padding: $font-size-base * 1.4 $font-size-base * 2;
      margin-top: $font-size-base * 0.5;

      @include font-size(0.95rem, 600, 1.4);
    }
  }

  @media screen and (max-width: $breakpoint-xs) {
    &-info {
      &-item {
        flex-direction: column;

        &__key {
          margin-bottom: $font-size-base * 0.5;
        }
      }
    }
  }
}
</style>
